// Reading / Listening practice attempt drill-in. Mirrors MockReviewPage per-
// question layout — verdict, options highlight, trap label, explanation — but
// scoped to a single practice set attempt. Reachable from Results > Practice
// via the "Review" button on R/L attempt rows.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Headphones } from 'lucide-react';

import { api } from '@/lib/api';
import { prettyTask } from '@/lib/labels';
import AppShell from '@/components/AppShell';
import QuestionReviewRow from '@/components/QuestionReviewRow';
import { cn } from '@/lib/utils';

const SECTION_ICON = { reading: BookOpen, listening: Headphones };
const when = (iso) => (iso ? `${iso.slice(0, 10)} · ${iso.slice(11, 16)}` : '');
const pctCls = (p) => (p >= 75 ? 'text-emerald-600' : p >= 50 ? 'text-indigo-600' : 'text-red-600');

export default function PracticeReviewPage() {
  const { exam, attempt_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.practiceReview(exam, attempt_id)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load this practice attempt.'));
  }, [exam, attempt_id]);

  if (error) return (
    <AppShell>
      <button
        type="button"
        onClick={() => navigate(`/${exam}/results?view=practice`)}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        data-testid="practice-review-back"
      >
        <ArrowLeft className="h-4 w-4" /> Results
      </button>
      <p className="mono-stat mt-6">{exam} · practice review</p>
      <h1 className="h-display mt-2 text-3xl">Couldn’t load this attempt.</h1>
      <p className="mt-2 text-sm text-slate-500">{error}</p>
    </AppShell>
  );
  if (!data) return <AppShell><p className="py-10 text-slate-400">Loading…</p></AppShell>;

  const Icon = SECTION_ICON[data.section] || BookOpen;
  const pct = data.n_total ? Math.round((data.n_correct / data.n_total) * 100) : 0;
  const title = data.set?.title || prettyTask(data.task_type);
  const stim = data.set?.stimulus || {};

  return (
    <AppShell>
      <button
        type="button"
        onClick={() => navigate(`/${exam}/results?view=practice&skill=${data.section}`)}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        data-testid="practice-review-back"
      >
        <ArrowLeft className="h-4 w-4" /> Results
      </button>

      <p className="mono-stat mt-6">{exam} · practice review</p>
      <h1 className="h-display mt-2 text-3xl" data-testid="practice-review-title">{title}</h1>
      <p className="mt-1 flex items-center gap-2 text-sm text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        <span className="capitalize">{data.section}</span>
        <span>·</span>
        <span>{when(data.at)}</span>
      </p>

      <div className="mt-8 grid max-w-5xl gap-6 lg:grid-cols-5">
        {/* LEFT — the passage / audio / image the questions are about */}
        <aside className="lg:col-span-2">
          <div className="border border-slate-200 bg-white" data-testid="practice-review-stimulus">
            <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">The passage</p>
            </div>
            <div className="space-y-4 px-5 py-5">
              {stim.title && (
                <p className="font-display text-[15px] font-semibold text-slate-900">{stim.title}</p>
              )}
              {stim.text && (
                <p className="whitespace-pre-wrap text-[15px] leading-7 text-slate-800">{stim.text}</p>
              )}
              {stim.audio_ref && (
                <p className="text-xs text-slate-500">Audio-based task · {stim.audio_ref}</p>
              )}
              {stim.image_ref && (
                <p className="text-xs text-slate-500">Image-based task · {stim.image_ref}</p>
              )}
              {!stim.text && !stim.title && !stim.audio_ref && !stim.image_ref && (
                <p className="text-sm text-slate-500">Stimulus is no longer available for this set.</p>
              )}
            </div>
          </div>
        </aside>

        {/* RIGHT — the score and every question with its verdict */}
        <section className="lg:col-span-3">
          <div className="border border-slate-200 bg-white" data-testid="practice-review-questions">
            <div className="flex items-baseline justify-between border-b border-slate-100 bg-slate-50/70 px-5 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {data.n_correct} of {data.n_total} correct
              </p>
              <p className={cn('h-display text-lg tabular-nums', pctCls(pct))} data-testid="practice-review-pct">{pct}%</p>
            </div>
            {(data.results || []).length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500" data-testid="practice-review-empty">
                {data.retired
                  ? 'This attempt used a practice set that has since been retired from the library, so we can’t reconstruct the questions. Your score is still counted in your progress.'
                  : 'No per-question detail available for this attempt.'}
              </p>
            ) : (
              <>
                {data.legacy && (
                  <div className="border-b border-amber-100 bg-amber-50/50 px-5 py-3 text-xs leading-5 text-amber-900" data-testid="practice-review-legacy-banner">
                    This attempt was recorded before per-question tracking was
                    enabled, so we don’t have your individual picks. Showing the
                    passage and the correct answers so you can still study it.
                  </div>
                )}
                {(data.results || []).map((row, i) => (
                  <QuestionReviewRow key={`${row.qid}-${i}`} row={row} index={i} />
                ))}
              </>
            )}
          </div>

          <div className="mt-6">
            <Link
              to={`/${exam}/results?view=practice&skill=${data.section}`}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
              data-testid="practice-review-back-bottom"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Results
            </Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
